import React, { useRef } from 'react';
import { Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import { Button, Modal, Row, Col } from 'antd';
import * as Yup from 'yup';

import SubmitButton from 'components/formik/SubmitButton';
import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { createRecruiter } from 'redux/reducers/recruiterSlice';
import useModalCloseOnNavigate from 'hooks/useModalCloseOnNavigate';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  visible: boolean;
  onClose: () => void;
};

interface FormValues {
  firstName: string;
  lastName: string;
}

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, 'Last name should be 120 characters maximum'),
});

export default function RecruiterModal(props: Props) {
  const { visible, onClose } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();

  useModalCloseOnNavigate(onClose);

  const initialValues: FormValues = {
    firstName: '',
    lastName: '',
  };

  const handleModalCancel = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    const { firstName, lastName } = values;

    const data = {
      firstName,
      lastName,
    };

    const result = await dispatch(createRecruiter(data));

    if (createRecruiter.fulfilled.match(result)) {
      BaseAlert.success('Internal member created successfully.');

      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <BaseModal
      title="Create an internal member"
      maxWidth={468}
      open={visible}
      onCancel={handleModalCancel}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <div className="modal-title">General information</div>

            <Row gutter={20}>
              <Col xs={24} lg={12}>
                <FormItem
                  name="firstName"
                  label="First name *"
                  data-id="newRecruiterFirstNameField"
                >
                  <Input name="firstName" placeholder="Enter first name" autoComplete="off" />
                </FormItem>
              </Col>

              <Col xs={24} lg={12}>
                <FormItem
                  name="lastName"
                  label="Last name *"
                  data-id="newRecruiterLastNameField"
                >
                  <Input name="lastName" placeholder="Enter last name" autoComplete="off" />
                </FormItem>
              </Col>
            </Row>
          </div>

          <BaseModalFooter>
            <Button onClick={handleModalCancel}>Cancel</Button>

            <SubmitButton data-id="createNewRecruiterBtn">Create</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
